import React from 'react';
import {dateToEpoch, dateWordShort} from "helper/helper";
import {useNavigate} from "react-router-dom";
import UseContext from "hooks/useContext";
import PerimeterName from "components/perimeterFilter/PerimeterName";
import {formatFullName} from "helper/personName";

const CardMission = ({data, isWaiting, showPerimeter = false}) => {
    const navigate = useNavigate()
    const {language} = UseContext()
    const d0 = dateToEpoch(new Date())
    const d1 = dateToEpoch(data?.duration?.datestart)
    const d2 = dateToEpoch(data?.duration?.dateend)

    //ALERT PRIMARY VALID
    const statutMission = () => {
        if (d0.getTime() > d2.getTime()) return 'ALERT'
        if ((d0.getTime() >= d1.getTime()) && (d0.getTime() <= d2.getTime())) return 'VALID'
        if (d0.getTime() < d1.getTime()) return 'PRIMARY'
    }

    const borderColors = {
        'ALERT': 'bg-red-500',
        'VALID': 'bg-green-500',
        'PRIMARY': 'bg-blue-500'
    }

    const MOTIF_LABELS = {
        '1': "Accroissement temporaire d’activité",
        '2': "En attente de recrutement",
        '3': "Remplacement d'un salarié absent",
        '4': "Contrat temporaire d'usage",
        '5': "Emploi à caractère saisonnier",
    };

    // Les missions en DB stockent souvent le motif dans des champs dédiés (pas forcément mission.motif)
    const getMotifLabelFromMission = (mission) => {
        if (!mission) return 'Non spécifié';

        // Nouveau format (rare): mission.motif = { type: '1', ... } ou string
        const motif = mission.motif;
        if (motif) {
            if (typeof motif === 'string') {
                // si c'est directement un type ("1".."5"), mapper
                if (MOTIF_LABELS[motif]) return MOTIF_LABELS[motif];
                return motif;
            }
            if (typeof motif === 'number') return MOTIF_LABELS[String(motif)] || 'Non spécifié';
            if (motif.type) return MOTIF_LABELS[String(motif.type)] || motif.title || motif.label || 'Non spécifié';
            if (motif.label) return motif.label;
            if (motif.title) return motif.title;
        }

        // Format API actuel: champs dédiés par type (voir backend processMotifEntries)
        if (mission.info_temporary_activity) return MOTIF_LABELS['1'];
        if (mission.awaiting_recruitement) return MOTIF_LABELS['2'];
        if (mission.replacement_employee) return MOTIF_LABELS['3'];
        if (mission.temporary_contract) return MOTIF_LABELS['4'];
        if (mission.seasonal_employment) return MOTIF_LABELS['5'];

        return 'Non spécifié';
    };

    return (
        <>
            {
                isWaiting ? (
                    <div className='bg-white border border-gray-200 rounded-md w-full p-4 flex items-center gap-4 animate-pulse relative overflow-hidden h-[88px]'>
                        <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-gray-200"></div>
                        <div className="flex flex-col flex-1 pl-3 pr-4 border-r border-gray-100 min-w-[220px] gap-2">
                            <div className="h-5 bg-gray-200 rounded w-3/4"></div>
                        </div>
                        <div className="flex flex-col flex-1 px-4 border-r border-gray-100 min-w-[180px] gap-2">
                            <div className="h-4 bg-gray-200 rounded w-20"></div>
                            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                        </div>
                        <div className="flex flex-col flex-1 px-4 border-r border-gray-100 min-w-[180px] gap-2">
                            <div className="h-4 bg-gray-200 rounded w-16"></div>
                            <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                        </div>
                        {showPerimeter && (
                            <div className="flex flex-col flex-1 px-4 border-r border-gray-100 min-w-[160px] gap-2">
                                <div className="h-4 bg-gray-200 rounded w-16"></div>
                                <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                            </div>
                        )}
                        <div className="flex flex-col flex-1 px-4 border-r border-gray-100 min-w-[200px] gap-2">
                            <div className="h-4 bg-gray-200 rounded w-20"></div>
                            <div className="h-4 bg-gray-200 rounded w-full"></div>
                        </div>
                        <div className="flex flex-col flex-1 px-4 min-w-[160px] gap-2">
                            <div className="h-4 bg-gray-200 rounded w-16"></div>
                            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                        </div>
                    </div>
                ) : (
                    <div 
                        className='bg-white border border-gray-200 rounded-md w-full cursor-pointer hover:bg-gray-50 active:bg-gray-100 transition-colors flex items-center p-4 relative overflow-hidden h-[88px]'
                        onClick={() => navigate(`/mission/${data._id}`)}
                    >
                        {/* Barre de statut latérale */}
                        <div className={`absolute left-0 top-0 bottom-0 w-1.5 ${borderColors[statutMission()] || 'bg-gray-300'}`}></div>

                        {/* Titre et Ville */}
                        <div className="flex flex-col flex-1 pl-3 pr-4 border-r border-gray-100 min-w-[220px]">
                            <h4 className="text-gray-900 font-semibold text-base truncate" title={data.title}>
                                {data.title}
                            </h4>
                            <div className="text-gray-500 text-sm truncate">
                                {data.address[0]?.city || 'Lieu non renseigné'}
                            </div>
                        </div>

                        {/* Intérimaire */}
                        <div className="flex flex-col flex-1 px-4 border-r border-gray-100 min-w-[180px]">
                            <span className="text-gray-900 font-medium text-sm">Intérimaire</span>
                            <span className="text-gray-500 text-sm truncate">
                                {data.userInfo[0]?.firstname ? formatFullName(data.userInfo[0].firstname, data.userInfo[0].lastname) : 'Non assigné'}
                            </span>
                        </div>

                        {/* Motif */}
                        <div className="flex flex-col flex-1 px-4 border-r border-gray-100 min-w-[180px]">
                            <span className="text-gray-900 font-medium text-sm">Motif</span>
                            <span className="text-gray-500 text-sm truncate">
                                {getMotifLabelFromMission(data) || 'Non spécifié'}
                            </span>
                        </div>

                        {showPerimeter && (
                            <div className="flex flex-col flex-1 px-4 border-r border-gray-100 min-w-[160px]">
                                <span className="text-gray-900 font-medium text-sm">
                                    {language.getValue("perimeters.listFilter.column")}
                                </span>
                                <span className="text-gray-500 text-sm truncate">
                                    <PerimeterName perimeter={data.perimeter}/>
                                </span>
                            </div>
                        )}

                        {/* Période */}
                        <div className="flex flex-col flex-1 px-4 border-r border-gray-100 min-w-[200px]">
                            <span className="text-gray-900 font-medium text-sm">Période</span>
                            <span className="text-gray-500 text-sm truncate">
                                {dateWordShort(data.duration.datestart)} &gt; {dateWordShort(data.duration.dateend)}
                            </span>
                        </div>

                        {/* Coût / Salaire */}
                        <div className="flex flex-col flex-1 px-4 min-w-[160px]">
                            <span className="text-gray-900 font-medium text-sm">Coût horaire</span>
                            <span className="text-gray-500 text-sm truncate">
                                {data.salary.wage_per_hour !== 0 ? `${data.salary.wage_per_hour} €` : 'Selon expérience'}
                            </span>
                        </div>
                    </div>
                )
            }
        </>
    );
};

export default CardMission;